import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { formatCurrency } from '@/utils/formatters'
import { WarningFilled } from '@ant-design/icons'
import type { Match } from '@api/useSupplierReconciliation/types/match'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Form, Select, Tooltip } from 'antd'
import { useMemo, useState } from 'react'
import type { SupplierReconciliationMetadataResponse } from '../../../../api/useMetadata/types'
import { useReasonCodes } from '../hooks/useReasonCodes'
import type { LabeledValue } from 'antd/es/select'
import { getUnmatchInvoicesAndBols } from '../utils/util'
import { useSupplierReconciliation } from '@/api/useSupplierReconciliation'
import { getCanUnmatch } from '../Detail/components/MatchDetailPane/util'

interface BulkChangeBarProps {
  selectedRows: Match[]
  onCancel: () => void
  onApprove: (rows: Match[], reason?: string) => Promise<void>
  onMatch: (
    matchId: string,
    links: {
      invoices: Array<{ invoice_number: string; invoice_id: string | null }>
      bols: Array<{ bol_number: string; bol_id: string | null }>
    },
    isUnmatch?: boolean
  ) => Promise<void>
  onMarkHold: (rows: Match[], reason?: string) => Promise<void>
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
}

export function BulkChangeBar({
  selectedRows,
  onCancel,
  onApprove,
  onMatch,
  onMarkHold,
  metadata,
  metadataLoading,
}: BulkChangeBarProps) {
  const { scopes } = useSecurityContext()
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [reason, setReason] = useState<LabeledValue | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Get separated reason codes using the new hook
  const { approvalReasonCodeOptions, holdReasonCodeOptions } = useReasonCodes(metadata)

  const { useBulkUnmatch } = useSupplierReconciliation()

  // Get the appropriate options based on the selected action
  const currentReasonCodeOptions = useMemo(() => {
    return selectedAction === 'approve' ? approvalReasonCodeOptions : holdReasonCodeOptions
  }, [selectedAction, approvalReasonCodeOptions, holdReasonCodeOptions])

  // Check if user has bulk approve permission
  const hasBulkApprovePermission = scopes?.supplier_rec?.bulk_approve || false

  // Check if we can approve - none should already be approved
  const hasApprovedRows = selectedRows.some((row) => row.status === 'approved')
  const hasUnmatchedRows = selectedRows.some((row) => row.status === 'unmatched')
  const canApprove = selectedRows.length > 0 && !hasApprovedRows && !hasUnmatchedRows && hasBulkApprovePermission
  const canMatch = selectedRows.length > 1 && !selectedRows.some((row) => row.status === 'approved')
  const canUnmatch = selectedRows.some(getCanUnmatch)

  // Check if we can mark hold - none should already be on hold
  const hasHoldRows = selectedRows.some((row) => row.status === 'hold')
  const canMarkHold = selectedRows.length > 0 && !hasHoldRows

  // Calculate totals for matching display
  const totals = useMemo(() => {
    if (selectedAction !== 'match' || selectedRows.length === 0) {
      return null
    }

    let invoiceTotal = 0
    let bolTotal = 0
    const productSet = new Set<string>()

    selectedRows.forEach((row) => {
      // Add invoice totals
      if (row.invoice_total) {
        invoiceTotal += row.invoice_total
      }

      // Add BOL totals
      if (row.bol_total) {
        bolTotal += row.bol_total
      }

      // Collect unique products
      if (row.products) {
        row.products.forEach((product) => productSet.add(product))
      }
    })

    // Calculate variance (invoice total - bol total)
    const variance = invoiceTotal - bolTotal
    const hasVarianceWarning = Math.abs(variance) >= 500

    return {
      products: Array.from(productSet),
      invoiceTotal,
      bolTotal,
      variance,
      hasVarianceWarning,
    }
  }, [selectedAction, selectedRows])

  // Get disabled reason messages
  const getApproveDisabledReason = () => {
    if (selectedRows.length === 0) return 'No rows selected'
    if (!hasBulkApprovePermission) return 'You do not have permission to bulk approve'
    if (hasApprovedRows) return 'Cannot approve - selection contains already approved matches'
    if (hasUnmatchedRows) return 'Cannot approve - selection contains unmatched items'
    return null
  }

  const getMatchDisabledReason = () => {
    if (selectedRows.length === 0) return 'No rows selected'
    if (selectedRows.length === 1) return 'Need at least 2 rows to match'
    if (selectedRows.some((row) => row.status === 'approved')) {
      return 'Cannot match - selection contains approved items'
    }
    return null
  }

  const getMarkHoldDisabledReason = () => {
    if (selectedRows.length === 0) return 'No rows selected'
    if (hasHoldRows) return 'Cannot mark hold - selection contains rows already on hold'
    return null
  }

  function handleActionChange(action: string) {
    setSelectedAction(action)
    if (action !== 'approve' && action !== 'mark_hold') {
      setReason(null)
    }
  }

  async function handleConfirm() {
    if (!selectedAction || selectedRows.length === 0) return
    setIsLoading(true)
    try {
      switch (selectedAction) {
        case 'approve':
          await onApprove(selectedRows, reason?.label?.toString())
          break
        case 'match':
          // Collect all invoices and BOLs from selected rows
          const targetMatch = selectedRows?.[0]
          const allInvoices: Array<{ invoice_number: string; invoice_id: string | null }> = []
          const allBols: Array<{ bol_number: string; bol_id: string | null }> = []

          selectedRows.forEach((row) => {
            // Add invoices from this row
            row.invoice_numbers?.forEach((inv) => {
              allInvoices.push({
                invoice_number: inv.number,
                invoice_id: inv.id,
              })
            })

            // Add BOLs from this row
            row.bol_numbers?.forEach((bol) => {
              allBols.push({
                bol_number: bol.number,
                bol_id: bol.id,
              })
            })
          })

          const filteredBols = allBols.filter((bol) => bol.bol_id !== null && bol.bol_id !== undefined)
          await onMatch(targetMatch.id, { invoices: allInvoices, bols: filteredBols })
          break
        case 'unmatch':
          await useBulkUnmatch.mutateAsync({
            body: { match_ids: selectedRows.map((row) => row.id) },
          })
          break
        case 'mark_hold':
          await onMarkHold(selectedRows, reason?.label?.toString())
          break
      }

      // After successful action, clear selection and exit bulk editing mode
      onCancel()
    } finally {
      setIsLoading(false)
      setSelectedAction(null)
      setReason(null)
    }
  }

  return (
    <Horizontal
      className='bg-1 p-3 mt-3 mb-3 border full-width'
      style={{
        borderRadius: '3px',
        height: '60px',
      }}
      verticalCenter
    >
      <Horizontal flex={1} verticalCenter gap={16} style={{ padding: '0 16px' }}>
        <Texto weight='bold'>
          {selectedRows.length} match{selectedRows.length !== 1 ? 'es' : ''} selected
        </Texto>

        <Form.Item label='Action' style={{ margin: 0, minWidth: 280 }}>
          <Select
            value={selectedAction}
            onChange={handleActionChange}
            placeholder='Select action'
            style={{ width: 200 }}
          >
            <Select.Option value='match' disabled={!canMatch}>
              <Tooltip title={getMatchDisabledReason()}>
                <span>Match</span>
              </Tooltip>
            </Select.Option>
            <Select.Option value='unmatch' disabled={!canUnmatch}>
              <Tooltip>
                <span>Unmatch</span>
              </Tooltip>
            </Select.Option>
            <Select.Option value='mark_hold' disabled={!canMarkHold}>
              <Tooltip title={getMarkHoldDisabledReason()}>
                <span>Mark Hold</span>
              </Tooltip>
            </Select.Option>
            <Select.Option value='approve' disabled={!canApprove}>
              <Tooltip title={getApproveDisabledReason()}>
                <span>Approve</span>
              </Tooltip>
            </Select.Option>
          </Select>
        </Form.Item>

        {(selectedAction === 'approve' || selectedAction === 'mark_hold') && (
          <Form.Item
            label={selectedAction === 'approve' ? 'Reason' : 'Hold Reason'}
            style={{ margin: 0, minWidth: 200 }}
          >
            <Select
              value={reason}
              onChange={(value) => setReason(value)}
              style={{ width: 180 }}
              options={currentReasonCodeOptions}
              placeholder={selectedAction === 'approve' ? 'Select reason code' : 'Select hold reason (optional)'}
              loading={metadataLoading}
              showSearch
              allowClear={selectedAction === 'mark_hold'}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
              disabled={currentReasonCodeOptions.length === 0 && !metadataLoading}
              labelInValue
            />
          </Form.Item>
        )}

        {selectedAction === 'match' && totals && (
          <Horizontal gap={24} style={{ marginLeft: 0 }}>
            <Horizontal gap={16}>
              <Texto style={{ whiteSpace: 'nowrap' }}>
                Products: <span style={{ fontWeight: 'bold' }}>{totals.products.join(', ') || 'None'}</span>
              </Texto>
              <Texto style={{ whiteSpace: 'nowrap' }}>
                Invoice Total: <span style={{ fontWeight: 'bold' }}>{formatCurrency(totals.invoiceTotal)}</span>
              </Texto>
              <Texto style={{ whiteSpace: 'nowrap' }}>
                BOL Total: <span style={{ fontWeight: 'bold' }}>{formatCurrency(totals.bolTotal)}</span>
              </Texto>
              <Horizontal gap={4} alignItems='center'>
                <Texto style={{ whiteSpace: 'nowrap' }}>
                  Variance: <span style={{ fontWeight: 'bold' }}>{formatCurrency(totals.variance)}</span>
                </Texto>
                {totals.hasVarianceWarning && <WarningFilled style={{ color: '#faad14', fontSize: 14 }} />}
              </Horizontal>
            </Horizontal>
          </Horizontal>
        )}
      </Horizontal>

      <Horizontal gap={8} style={{ paddingRight: 16 }}>
        <GraviButton onClick={onCancel}>Cancel</GraviButton>
        <GraviButton
          theme1
          onClick={handleConfirm}
          disabled={!selectedAction || selectedRows.length === 0 || (selectedAction === 'approve' && !reason)}
          loading={isLoading}
        >
          Confirm
        </GraviButton>
      </Horizontal>
    </Horizontal>
  )
}
